// src/hooks/useScheduleRequests.js
// Surfaces all schedule-related items (pending requests awaiting Accept/Reject/
// Counter, admin approval cards, and status updates) so they live in the
// notifications bell instead of floating toasts.
import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTournaments } from '../context/TournamentContext';
import toast from 'react-hot-toast';

// Timezone-aware offset helper (ported from TournamentNotifications)
const getTimezoneOffset = (date, timeZone) => {
  try {
    if (!date || isNaN(date.getTime()) || !timeZone) return 0;
    const utcDate = new Date(date.toLocaleString('en-CA', { timeZone: 'UTC' }));
    const localDate = new Date(date.toLocaleString('en-CA', { timeZone }));
    if (isNaN(utcDate.getTime()) || isNaN(localDate.getTime())) return 0;
    return localDate.getTime() - utcDate.getTime();
  } catch (error) {
    return 0;
  }
};

// Format a proposed date/time, converting from the proposer's timezone to the
// viewer's local timezone (ported from TournamentNotifications).
export const formatScheduleDateTime = (date, time, sourceTimezone = 'Europe/Lisbon') => {
  if (!date || !time) return 'TBD';
  try {
    if (typeof date !== 'string' || typeof time !== 'string') return 'Invalid date/time';

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    let cleanTime = time;
    if (time.includes(':')) {
      const timeParts = time.split(':');
      if (timeParts.length >= 3) {
        cleanTime = `${timeParts[0]}:${timeParts[1]}:${timeParts[2]}`;
      } else if (timeParts.length === 2) {
        cleanTime = `${time}:00`;
      }
    }

    const dateObj = new Date(`${date}T${cleanTime}`);
    if (isNaN(dateObj.getTime())) return `${date} ${time}`;

    if (sourceTimezone === userTimezone) {
      return new Intl.DateTimeFormat(navigator.language || 'en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
      }).format(dateObj);
    }

    try {
      const sourceDate = new Date(`${date}T${cleanTime}`);
      const userFormatter = new Intl.DateTimeFormat(navigator.language || 'en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: userTimezone, timeZoneName: 'short'
      });
      const sourceFormatter = new Intl.DateTimeFormat(navigator.language || 'en-US', {
        hour: '2-digit', minute: '2-digit', timeZone: sourceTimezone, timeZoneName: 'short'
      });
      const offsetDiff = getTimezoneOffset(sourceDate, userTimezone) - getTimezoneOffset(sourceDate, sourceTimezone);
      const adjustedDate = new Date(sourceDate.getTime() + offsetDiff);
      return `${userFormatter.format(adjustedDate)} (${sourceFormatter.format(sourceDate)} proposed)`;
    } catch (timezoneError) {
      return new Intl.DateTimeFormat(navigator.language || 'en-US', {
        weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      }).format(dateObj);
    }
  } catch (error) {
    return date && time ? `${date} ${time}` : 'TBD';
  }
};

const useScheduleRequests = () => {
  const { user } = useAuth();
  const {
    loadScheduleRequests,
    loadScheduleNotifications,
    respondToSchedule,
    approveSchedule,
    rejectSchedule,
    markScheduleNotificationRead
  } = useTournaments();

  const [scheduleRequests, setScheduleRequests] = useState([]);       // Accept / Reject / Counter
  const [scheduleApprovals, setScheduleApprovals] = useState([]);     // Admin "Needs Approval"
  const [scheduleUpdates, setScheduleUpdates] = useState([]);         // Info: accepted / rejected

  // Items already acted on this session — the RPC may still return them as
  // active (e.g. pending the other side / admin approval), so we hard-hide them.
  const resolvedRequestIds = useRef(new Set());
  const resolvedNotificationIds = useRef(new Set());

  const reload = useCallback(async () => {
    if (!user?.id) {
      setScheduleRequests([]);
      setScheduleApprovals([]);
      setScheduleUpdates([]);
      return;
    }
    try {
      const [requestsResult, notificationsResult] = await Promise.all([
        loadScheduleRequests(),
        loadScheduleNotifications()
      ]);

      if (requestsResult.success) {
        setScheduleRequests(
          requestsResult.data.filter(r =>
            r.can_respond &&
            (r.status === 'pending' || r.status === 'counter_proposed') &&
            !resolvedRequestIds.current.has(r.request_id)
          )
        );
      } else {
        setScheduleRequests([]);
      }

      if (notificationsResult.success) {
        const notifications = notificationsResult.data.filter(n => !resolvedNotificationIds.current.has(n.id));
        // Admin approval requests (tournament owner action)
        setScheduleApprovals(
          notifications.filter(n =>
            n.notification_type === 'schedule_admin_decision' &&
            !n.read &&
            n.title === 'Schedule Request Needs Approval'
          )
        );
        // Status updates (approved / rejected responses) — dismissible
        setScheduleUpdates(
          notifications.filter(n =>
            (n.notification_type === 'schedule_admin_decision' || n.notification_type === 'schedule_response') &&
            !n.read &&
            n.title !== 'Schedule Request Needs Approval'
          )
        );
      } else {
        setScheduleApprovals([]);
        setScheduleUpdates([]);
      }
    } catch (error) {
      console.error('Error loading schedule data:', error);
      setScheduleRequests([]);
      setScheduleApprovals([]);
      setScheduleUpdates([]);
    }
  }, [user?.id, loadScheduleRequests, loadScheduleNotifications]);

  // Initial load + refresh when other parts of the app touch scheduling
  useEffect(() => {
    reload();
    const handler = () => reload();
    window.addEventListener('scheduleActionUpdate', handler);
    return () => window.removeEventListener('scheduleActionUpdate', handler);
  }, [reload]);

  // Respond to a pending request (accept / reject / counter_propose)
  const respond = useCallback(async (requestId, response, responseData = {}) => {
    try {
      const result = await respondToSchedule(requestId, response, responseData);
      if (result.success) {
        const responseText = response === 'accept' ? 'accepted'
          : response === 'reject' ? 'rejected' : 'counter-proposed';
        toast.success(`Schedule request ${responseText} successfully`);
        resolvedRequestIds.current.add(requestId);
        setScheduleRequests(prev => prev.filter(r => r.request_id !== requestId));
        reload();
        window.dispatchEvent(new Event('scheduleActionUpdate'));
      } else {
        toast.error(result.error || 'Failed to respond to schedule request');
      }
      return result;
    } catch (error) {
      console.error('Error responding to schedule request:', error);
      toast.error('An error occurred while responding to the schedule request');
      return { success: false };
    }
  }, [respondToSchedule, reload]);

  // Admin: approve / reject a schedule request needing approval
  const approveApproval = useCallback(async (notification) => {
    try {
      const result = await approveSchedule(notification.schedule_request_id);
      if (result.success) {
        toast.success('Schedule request approved!');
        resolvedNotificationIds.current.add(notification.id);
        setScheduleApprovals(prev => prev.filter(n => n.id !== notification.id));
      } else {
        toast.error(result.error || 'Failed to approve schedule');
      }
    } catch (error) {
      toast.error('Error approving schedule');
    }
  }, [approveSchedule]);

  const rejectApproval = useCallback(async (notification) => {
    try {
      const result = await rejectSchedule(notification.schedule_request_id);
      if (result.success) {
        toast.success('Schedule request rejected');
        resolvedNotificationIds.current.add(notification.id);
        setScheduleApprovals(prev => prev.filter(n => n.id !== notification.id));
      } else {
        toast.error(result.error || 'Failed to reject schedule');
      }
    } catch (error) {
      toast.error('Error rejecting schedule');
    }
  }, [rejectSchedule]);

  // Dismiss an informational status update
  const dismissUpdate = useCallback(async (notification) => {
    resolvedNotificationIds.current.add(notification.id);
    setScheduleUpdates(prev => prev.filter(n => n.id !== notification.id));
    try {
      await markScheduleNotificationRead(notification.id);
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  }, [markScheduleNotificationRead]);

  const scheduleCount = scheduleRequests.length + scheduleApprovals.length + scheduleUpdates.length;

  return {
    scheduleRequests,
    scheduleApprovals,
    scheduleUpdates,
    scheduleCount,
    reload,
    respond,
    approveApproval,
    rejectApproval,
    dismissUpdate,
    formatDateTime: formatScheduleDateTime
  };
};

export default useScheduleRequests;
